import { fetchAPI } from '.'

import { AuditAction, AuditLogsPortalLinkResponse, WorkosAuditLogTableResponse } from '@/api/generated/api'

// Server rejects a window of MAX_AUDIT_WINDOW_DAYS or more (aria-web audit_logs.py).
export const AUDIT_LOG_MAX_WINDOW_DAYS = 92

// The on-screen viewer's page-size picker only ever offers these three values — matches the
// backend's own WORKOS_AUDIT_EVENTS_PAGE_SIZES (aria-web's api/v1/audit_logs.py).
export const WORKOS_AUDIT_EVENTS_PAGE_SIZES = [20, 50, 100] as const
export type WorkosAuditEventsPageSize = (typeof WORKOS_AUDIT_EVENTS_PAGE_SIZES)[number]

export interface GetWorkosAuditEventsParams {
  start: string
  end: string
  eventTypes?: AuditAction[]
  before?: string
  limit?: WorkosAuditEventsPageSize
}

// This is the Events tab's read path — WorkOS, not the local derived-read table (no UI consumer
// left; see aria-web's services/audit_log/service.py). A 404 means the enterprise has no
// workos_org_id — treat that as "not opted in", not an error. `before` is our own date-boundary
// pagination cursor, not WorkOS's own (which doesn't advance correctly).
export async function getWorkosAuditEvents(params: GetWorkosAuditEventsParams) {
  const search = new URLSearchParams()
  search.set('start', params.start)
  search.set('end', params.end)
  for (const eventType of params.eventTypes ?? []) {
    search.append('event_types', eventType)
  }
  if (params.before !== undefined) search.set('before', params.before)
  if (params.limit !== undefined) search.set('limit', String(params.limit))

  return fetchAPI<WorkosAuditLogTableResponse>({
    method: 'GET',
    url: `/audit-logs/workos?${search.toString()}`,
  })
}

// Fallback for when the embedded Events tab's own read fails — a WorkOS-hosted page the admin
// can open instead. Read-only: 404s with the same "not opted in" contract as getWorkosAuditEvents.
export async function createAuditLogsPortalLink() {
  return fetchAPI<AuditLogsPortalLinkResponse>({
    method: 'POST',
    url: '/audit-logs/workos/portal-link',
  })
}
